import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Download, Loader2, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { paraBooleano } from '@/lib/utils';
import type { RespostaPaginada } from '@/components/paginacao';
import {
  ROTULOS_TIPO,
  TIPOS_SOLICITACAO,
  type Procedimento,
  type TipoSolicitacao,
} from '@/features/admin/tipos';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { ImportarProcedimentosSigtap } from '@/components/admin/importar-procedimentos-sigtap';
import { BarraBusca } from '@/components/barra-busca';
import { Paginacao } from '@/components/paginacao';
import { Botao } from '@/components/ui/button';
import { Entrada, Selecao } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Etiqueta } from '@/components/ui/badge';
import {
  Tabela,
  TabelaCabecalho,
  TabelaCelula,
  TabelaCorpo,
  TabelaLinha,
  TabelaTitulo,
  TabelaVazia,
} from '@/components/ui/table';
import { EsqueletoTabela } from '@/components/ui/skeleton';
import {
  Dialogo,
  DialogoCabecalho,
  DialogoConteudo,
  DialogoCorpo,
  DialogoDescricao,
  DialogoRodape,
  DialogoTitulo,
} from '@/components/ui/dialog';

const esquema = z.object({
  nome: z.string().min(3, 'Informe o nome do procedimento.'),
  tipo: z.enum(TIPOS_SOLICITACAO, { message: 'Selecione o tipo.' }),
  codigo: z.string().optional(),
  ativo: z.boolean().optional(),
});

type Formulario = z.infer<typeof esquema>;

const COLUNAS = 5;

export function PaginaProcedimentos() {
  const queryClient = useQueryClient();
  const [pagina, setPagina] = React.useState(1);
  const [busca, setBusca] = React.useState('');
  const [filtroTipo, setFiltroTipo] = React.useState<'' | TipoSolicitacao>('');
  const [emEdicao, setEmEdicao] = React.useState<Procedimento | null>(null);
  const [dialogoAberto, setDialogoAberto] = React.useState(false);
  const [importando, setImportando] = React.useState(false);

  const parametros = new URLSearchParams({ pagina: String(pagina), limite: '20' });
  if (busca) parametros.set('busca', busca);
  if (filtroTipo) parametros.set('tipo', filtroTipo);

  const { data, isPending } = useQuery({
    queryKey: ['procedimentos', pagina, busca, filtroTipo],
    queryFn: () =>
      api.get<RespostaPaginada<Procedimento>>(`/procedimentos?${parametros.toString()}`),
  });

  const formulario = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: { nome: '', tipo: 'EXAME', codigo: '' },
  });

  const abrirCriacao = () => {
    setEmEdicao(null);
    formulario.reset({ nome: '', tipo: 'EXAME', codigo: '' });
    setDialogoAberto(true);
  };

  const abrirEdicao = (procedimento: Procedimento) => {
    setEmEdicao(procedimento);
    formulario.reset({
      nome: procedimento.nome,
      tipo: procedimento.tipo,
      codigo: procedimento.codigo ?? '',
      ativo: procedimento.ativo,
    });
    setDialogoAberto(true);
  };

  const salvar = useMutation({
    mutationFn: (dados: Formulario) => {
      const corpo = { ...dados, codigo: dados.codigo || undefined };

      return emEdicao
        ? api.patch<Procedimento>(`/procedimentos/${emEdicao.id}`, corpo)
        : api.post<Procedimento>('/procedimentos', { ...corpo, ativo: undefined });
    },
    onSuccess: async () => {
      toast.success(emEdicao ? 'Procedimento atualizado.' : 'Procedimento cadastrado.');
      setDialogoAberto(false);
      await queryClient.invalidateQueries({ queryKey: ['procedimentos'] });
    },
    onError: (erro) => {
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível salvar.');
    },
  });

  const aoEnviar = formulario.handleSubmit((dados) => salvar.mutate(dados));

  return (
    <>
      <CabecalhoPagina
        titulo="Procedimentos e exames"
        descricao="Catálogo dos tipos de solicitação: consultas, exames e procedimentos."
        acoes={
          <div className="flex flex-wrap gap-2">
            <Botao variante="contorno" onClick={() => setImportando(true)}>
              <Download aria-hidden="true" />
              Importar do SIGTAP
            </Botao>
            <Botao onClick={abrirCriacao}>
              <Plus aria-hidden="true" />
              Novo procedimento
            </Botao>
          </div>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <BarraBusca
          valor={busca}
          aoMudar={(valor) => {
            setBusca(valor);
            setPagina(1);
          }}
          placeholder="Buscar por nome ou código"
        />
        <Selecao
          value={filtroTipo}
          onChange={(evento) => {
            setFiltroTipo(evento.target.value as '' | TipoSolicitacao);
            setPagina(1);
          }}
          aria-label="Filtrar por tipo"
          className="w-full sm:w-48"
        >
          <option value="">Todos os tipos</option>
          {TIPOS_SOLICITACAO.map((tipo) => (
            <option key={tipo} value={tipo}>
              {ROTULOS_TIPO[tipo]}
            </option>
          ))}
        </Selecao>
      </div>

      <Tabela>
        <TabelaCabecalho>
          <TabelaLinha>
            <TabelaTitulo>Nome</TabelaTitulo>
            <TabelaTitulo>Tipo</TabelaTitulo>
            <TabelaTitulo>Código</TabelaTitulo>
            <TabelaTitulo>Situação</TabelaTitulo>
            <TabelaTitulo className="text-right">Ações</TabelaTitulo>
          </TabelaLinha>
        </TabelaCabecalho>
        <TabelaCorpo>
          {isPending && <EsqueletoTabela colunas={COLUNAS} />}

          {!isPending && data?.dados.length === 0 && (
            <TabelaVazia colunas={COLUNAS}>Nenhum procedimento encontrado.</TabelaVazia>
          )}

          {data?.dados.map((procedimento) => (
            <TabelaLinha key={procedimento.id}>
              <TabelaCelula rotulo="Nome" className="font-medium">
                {procedimento.nome}
              </TabelaCelula>
              <TabelaCelula rotulo="Tipo">
                <Etiqueta>{ROTULOS_TIPO[procedimento.tipo]}</Etiqueta>
              </TabelaCelula>
              <TabelaCelula rotulo="Código" className="text-silenciado-texto tabular-nums">
                {procedimento.codigo ?? '—'}
                {procedimento.competenciaSigtap && (
                  <span className="block text-xs">
                    SIGTAP {procedimento.competenciaSigtap.slice(4, 6)}/
                    {procedimento.competenciaSigtap.slice(0, 4)}
                  </span>
                )}
              </TabelaCelula>
              <TabelaCelula rotulo="Situação">
                <Etiqueta tom={procedimento.ativo ? 'sucesso' : 'neutro'}>
                  {procedimento.ativo ? 'Ativo' : 'Inativo'}
                </Etiqueta>
              </TabelaCelula>
              <TabelaCelula className="text-right">
                <Botao
                  variante="fantasma"
                  tamanho="sm"
                  onClick={() => abrirEdicao(procedimento)}
                  aria-label={`Editar ${procedimento.nome}`}
                >
                  <Pencil aria-hidden="true" />
                  Editar
                </Botao>
              </TabelaCelula>
            </TabelaLinha>
          ))}
        </TabelaCorpo>
      </Tabela>

      {data && <Paginacao meta={data.meta} aoMudarPagina={setPagina} />}

      <ImportarProcedimentosSigtap aberto={importando} aoFechar={() => setImportando(false)} />

      <Dialogo open={dialogoAberto} onOpenChange={setDialogoAberto}>
        <DialogoConteudo>
          <DialogoCabecalho>
            <DialogoTitulo>{emEdicao ? 'Editar procedimento' : 'Novo procedimento'}</DialogoTitulo>
            <DialogoDescricao>
              O par nome + tipo é único: o mesmo nome pode existir como exame e como procedimento.
            </DialogoDescricao>
          </DialogoCabecalho>

          <form onSubmit={(evento) => void aoEnviar(evento)} noValidate>
            <DialogoCorpo>
              <Campo
                id="proc-nome"
                rotulo="Nome"
                obrigatorio
                erro={formulario.formState.errors.nome?.message}
              >
                <Entrada autoFocus {...formulario.register('nome')} />
              </Campo>

              <Campo
                id="proc-tipo"
                rotulo="Tipo"
                obrigatorio
                erro={formulario.formState.errors.tipo?.message}
              >
                <Selecao {...formulario.register('tipo')}>
                  {TIPOS_SOLICITACAO.map((tipo) => (
                    <option key={tipo} value={tipo}>
                      {ROTULOS_TIPO[tipo]}
                    </option>
                  ))}
                </Selecao>
              </Campo>

              <Campo id="proc-codigo" rotulo="Código" ajuda="Código na tabela oficial, se houver.">
                <Entrada {...formulario.register('codigo')} />
              </Campo>

              {emEdicao && (
                <Campo id="proc-ativo" rotulo="Situação">
                  <Selecao
                    {...formulario.register('ativo', {
                      setValueAs: paraBooleano,
                    })}
                  >
                    <option value="true">Ativo</option>
                    <option value="false">Inativo</option>
                  </Selecao>
                </Campo>
              )}
            </DialogoCorpo>

            <DialogoRodape>
              <Botao variante="contorno" onClick={() => setDialogoAberto(false)}>
                Cancelar
              </Botao>
              <Botao type="submit" disabled={salvar.isPending}>
                {salvar.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
                Salvar
              </Botao>
            </DialogoRodape>
          </form>
        </DialogoConteudo>
      </Dialogo>
    </>
  );
}
